import { useCallback, useState } from 'react';
import { submitQuiz } from '../api/client';
import { QUESTIONS } from '../data/questions';

export type QuizPhase =
  | 'intro'
  | 'quiz'
  | 'lead_capture'
  | 'submitting'
  | 'complete'
  | 'error';

interface QuizState {
  phase: QuizPhase;
  currentIndex: number;
  answers: (number | null)[];
  submissionId: string | null;
  error: string | null;
}

const INITIAL_STATE: QuizState = {
  phase: 'intro',
  currentIndex: 0,
  answers: new Array<null>(QUESTIONS.length).fill(null),
  submissionId: null,
  error: null,
};

export function useQuiz() {
  const [state, setState] = useState<QuizState>(INITIAL_STATE);

  const startQuiz = useCallback(() => {
    setState((s) => ({ ...s, phase: 'quiz', currentIndex: 0 }));
  }, []);

  const selectAnswer = useCallback((optionIndex: number) => {
    setState((s) => {
      if (s.phase !== 'quiz') return s;
      const answers = [...s.answers];
      answers[s.currentIndex] = optionIndex;

      const isLast = s.currentIndex === QUESTIONS.length - 1;
      if (isLast) {
        return { ...s, answers, phase: 'lead_capture' };
      }
      return { ...s, answers, currentIndex: s.currentIndex + 1 };
    });
  }, []);

  const goToPrevious = useCallback(() => {
    setState((s) => {
      if (s.phase === 'error') {
        return { ...s, phase: 'lead_capture', error: null };
      }
      if (s.phase === 'lead_capture') {
        return { ...s, phase: 'quiz', currentIndex: QUESTIONS.length - 1 };
      }
      if (s.phase === 'quiz' && s.currentIndex > 0) {
        return { ...s, currentIndex: s.currentIndex - 1 };
      }
      return s;
    });
  }, []);

  const submitLead = useCallback(
    async (name: string, email: string) => {
      if (state.answers.some((a) => a === null)) {
        setState((s) => ({ ...s, phase: 'error', error: 'Please answer all questions before submitting.' }));
        return;
      }
      const answers = state.answers as number[];
      setState((s) => ({ ...s, phase: 'submitting', error: null }));

      try {
        const result = await submitQuiz(name, email, answers);
        setState((s) => ({
          ...s,
          phase: 'complete',
          submissionId: result.id,
        }));
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Something went wrong. Please try again.';
        setState((s) => ({ ...s, phase: 'error', error: message }));
      }
    },
    [state.answers],
  );

  const reset = useCallback(() => {
    setState(INITIAL_STATE);
  }, []);

  return {
    state,
    currentQuestion:
      state.phase === 'quiz' ? QUESTIONS[state.currentIndex] : null,
    questionNumber: state.currentIndex + 1,
    totalQuestions: QUESTIONS.length,
    startQuiz,
    selectAnswer,
    goToPrevious,
    submitLead,
    reset,
  };
}
